import { useMemo } from "react";
import { AuditSession, Procedure } from "../models/audit/auditSession";
import { MultiselectOption } from "../components/ui/documentsMultiselect";
import { isDerivedChildDocument } from "../lib/documentChildTypes";

interface DocumentData {
  matched: (AuditSession["documents"][0] & {
    category?: Procedure["requiredDocuments"][0];
  })[];
  unmatched: (AuditSession["documents"][0] & {
    category?: Procedure["requiredDocuments"][0];
  })[];
  sampleOptions: (MultiselectOption & { sampleNumber?: number })[];
  categoryMap: Map<string, Procedure["requiredDocuments"][0]>;
  /** Document id to the ids of the source rows matched to it. */
  sampleValuesMap: Map<string, string[]>;
}

/**
 * useDocumentData
 *
 * Computes various mappings and convenience collections that are used when
 * displaying and interacting with audit session documents.
 */
export const useDocumentData = (
  auditSession?: AuditSession | null,
): DocumentData => {
  return useMemo(() => {
    if (!auditSession?.documents) {
      return {
        matched: [],
        unmatched: [],
        sampleOptions: [],
        categoryMap: new Map(),
        sampleValuesMap: new Map(),
      } as DocumentData;
    }

    // Build document-to-samples mapping and category assignments
    const sampleValuesMap = new Map<string, string[]>();
    const categoryMap = new Map<string, Procedure["requiredDocuments"][0]>();
    const matchedDocumentIds = new Set<string>();

    for (const sourceRow of auditSession.sourceRows || []) {
      for (const matchItem of sourceRow.matchItems || []) {
        const docId = matchItem.sourceDocument.id;
        matchedDocumentIds.add(docId);

        // The row id, not its sample number: the number is nullable and nothing
        // enforces uniqueness within a session, so the id is the only stable
        // handle on a row. It is also the currency of the Sample Match control,
        // which selects options by row id and writes row ids back.
        const matchedRowIds = sampleValuesMap.get(docId);
        if (matchedRowIds) {
          matchedRowIds.push(sourceRow.id);
        } else {
          sampleValuesMap.set(docId, [sourceRow.id]);
        }

        // Extract category from assignments
        for (const assignment of matchItem.assignments || []) {
          const requiredDoc = auditSession.procedure.requiredDocuments.find(
            (doc) => doc.id === assignment.procedureDocumentId,
          );
          if (requiredDoc) {
            categoryMap.set(docId, requiredDoc);
            break; // Take first assignment
          }
        }
      }
    }

    // Sample options for dropdowns
    const sampleOptions: (MultiselectOption & {
      sampleNumber?: number;
    })[] =
      auditSession.sourceRows?.map((row) => ({
        value: row.id,
        sampleNumber: row.sampleNumber || undefined,
        label: row.sampleNumber
          ? `Sample ${row.sampleNumber}`
          : `Row ${row.id.slice(0, 8)}`,
      })) || [];

    // Split documents into matched/unmatched and add category info
    const matched: (AuditSession["documents"][0] & {
      category?: Procedure["requiredDocuments"][0];
    })[] = [];
    const unmatched: (AuditSession["documents"][0] & {
      category?: Procedure["requiredDocuments"][0];
    })[] = [];

    for (const doc of auditSession.documents) {
      const docId = doc.documentId || doc.id;
      if (doc?.creatorType === "agent") {
        continue;
      }
      const enrichedDoc = {
        ...doc,
        category: categoryMap.get(docId) || undefined,
      } as typeof doc & { category?: Procedure["requiredDocuments"][0] };

      if (
        doc.fetchableDocument == null ||
        !isDerivedChildDocument(doc.fetchableDocument)
      ) {
        if (matchedDocumentIds.has(docId)) {
          matched.push(enrichedDoc);
        } else {
          unmatched.push(enrichedDoc);
        }
      }
    }

    return {
      matched,
      unmatched,
      sampleOptions,
      categoryMap,
      sampleValuesMap,
    };
  }, [auditSession]);
};
